import React, { useState, useEffect } from 'react';
import { ErrorBoundary as ReactErrorBoundary, FallbackProps } from 'react-error-boundary';
import { Box, Typography, Button, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';

interface ErrorBoundaryProps {
    children: React.ReactNode;
}

function ErrorFallback({ error, resetErrorBoundary }: Readonly<FallbackProps>) {
    const { t } = useTranslation();
    const typedError = error as Error;
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 4, gap: 2, backgroundColor: '#ffffff', width: '100%', height: '100%' }}>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 48 }} />
            <Typography variant="h6" color="error">{t('transitionJourney.errorBoundary.somethingWrong')}</Typography>
            <Accordion sx={{ width: 'calc(100vw - 48px)', margin: '16px' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body2">{t('transitionJourney.errorBoundary.errorDetails')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12, backgroundColor: '#f5f5f5', padding: 2, borderRadius: 1, height: '300px', overflowY: 'scroll' }}>
                        <Typography variant="body2" fontWeight="bold">{t('transitionJourney.errorBoundary.name')}</Typography>
                        <Typography variant="body2" mb={1}>{typedError?.name}</Typography>
                        <Typography variant="body2" fontWeight="bold">{t('transitionJourney.errorBoundary.message')}</Typography>
                        <Typography variant="body2" mb={1}>{typedError?.message}</Typography>
                        <Typography variant="body2" fontWeight="bold">{t('transitionJourney.errorBoundary.stack')}</Typography>
                        <Typography variant="body2" component="pre" sx={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {typedError?.stack}
                        </Typography>
                    </Box>
                </AccordionDetails>
            </Accordion>
            <Button variant="outlined" onClick={resetErrorBoundary}>{t('transitionJourney.errorBoundary.retry')}</Button>
        </Box>
    );
}

function ErrorBoundary({ children }: Readonly<ErrorBoundaryProps>) {
    const [asyncError, setAsyncError] = useState<Error | null>(null);
    const [resetKey, setResetKey] = useState(0);

    const handleReset = () => {
        setAsyncError(null);
        setResetKey(k => k + 1);
        window.location.reload();
    };

    useEffect(() => {
        const dismissCRAOverlay = () => {
            const overlay = document.querySelector('iframe[id^="webpack-dev-server-client-overlay"]') as HTMLIFrameElement | null;
            if (overlay) overlay.style.display = 'none';
        };

        const onUnhandledRejection = (event: PromiseRejectionEvent) => {
            event.preventDefault();
            setAsyncError(event.reason instanceof Error ? event.reason : new Error(String(event.reason)));
            setTimeout(dismissCRAOverlay, 0);
        };
        const onError = (event: ErrorEvent) => {
            event.preventDefault();
            setAsyncError(event.error instanceof Error ? event.error : new Error(event.message));
            setTimeout(dismissCRAOverlay, 0);
        };

        window.addEventListener('unhandledrejection', onUnhandledRejection);
        window.addEventListener('error', onError);
        return () => {
            window.removeEventListener('unhandledrejection', onUnhandledRejection);
            window.removeEventListener('error', onError);
        };
    }, []);

    if (asyncError) {
        return <ErrorFallback error={asyncError} resetErrorBoundary={handleReset} />;
    }

    return (
        <ReactErrorBoundary
            key={resetKey}
            FallbackComponent={ErrorFallback}
            onError={(error: unknown, info: React.ErrorInfo) => console.error('ErrorBoundary caught:', error, info)}
        >
            {children}
        </ReactErrorBoundary>
    );
}

export default ErrorBoundary;
